import { HighlightText } from '@/components/common/HighlightText';
import { StatusBadge } from '@/components/common/StatusBadge';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS, SOFT_ACCENT_MARK_CLASS } from '@/styles/accentMark';
import { formatCount } from '@/utils/number';
import type { MenuLabelUnit } from '../utils/menuLabelRows';

interface MenuUnitButtonProps {
  unit: MenuLabelUnit;
  isSelected: boolean;
  searchText: string;
  onSelect: (menuKey: string) => void;
}

function MenuUnitButton({ unit, isSelected, searchText, onSelect }: MenuUnitButtonProps) {
  const Icon = unit.icon;
  return (
    <button
      type="button"
      onClick={() => onSelect(unit.unit.menuKey)}
      aria-current={isSelected ? 'true' : undefined}
      className={cn(
        'flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors outline-none hover:bg-primary/8 focus-visible:ring-2 focus-visible:ring-ring/50',
        isSelected && 'bg-primary/16 hover:bg-primary/16',
      )}
    >
      <span
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-lg',
          isSelected ? ACCENT_MARK_CLASS : SOFT_ACCENT_MARK_CLASS,
        )}
      >
        <Icon className="size-4" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold">
          <HighlightText text={unit.unit.shown} highlightedText={searchText} />
        </span>
        <span className="block text-xs text-muted-foreground">
          {formatCount(unit.pageCount, 'page')}
        </span>
      </span>
      {unit.renamedCount > 0 ? (
        <StatusBadge tone="info">{`${String(unit.renamedCount)} renamed`}</StatusBadge>
      ) : null}
      {unit.hasUnsaved ? (
        <span className="size-2 shrink-0 rounded-full bg-warning-foreground">
          <span className="sr-only">Has unsaved changes</span>
        </span>
      ) : null}
    </button>
  );
}

interface MenuUnitListProps {
  units: MenuLabelUnit[];
  selectedKey: string;
  searchText: string;
  onSelect: (menuKey: string) => void;
}

export function MenuUnitList({ units, selectedKey, searchText, onSelect }: MenuUnitListProps) {
  return (
    <nav
      aria-label="Menus"
      className="flex max-h-72 min-h-0 flex-col gap-0.5 overflow-y-auto rounded-2xl bg-card p-2 shadow-sm ring-1 ring-primary/10 lg:max-h-none"
    >
      {units.map((unit) => (
        <MenuUnitButton
          key={unit.unit.menuKey}
          unit={unit}
          isSelected={unit.unit.menuKey === selectedKey}
          searchText={searchText}
          onSelect={onSelect}
        />
      ))}
    </nav>
  );
}
